"use client";

import Link from "next/link";

export default function HomePageClient() {

  return (
    <>
      {/* ── GLOBAL STYLES ── */}
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Bebas+Neue&family=DM+Sans:ital,wght@0,400;0,500;0,600;1,400&display=swap');

        :root {
          --navy:   #0F172A;
          --navy2:  #1e293b;
          --navy3:  #64748b;
          --slate:  #e2e8f0;
          --muted:  #cbd5e1;
          --dim:    #e2e8f0;
          --light:  #f1f5f9;
          --bright: #f1f5f9;
          --sky:    #38BDF8;
          --amber:  #F59E0B;
          --green:  #4ADE80;
          --sky-bg: #0c2f4a;
          --amb-bg: #2a1f08;
          --grn-bg: #0a2416;
        }

        html { scroll-behavior: smooth; }

        body {
          background: var(--navy);
          color: var(--bright);
          font-family: 'DM Sans', sans-serif;
          line-height: 1.6;
          overflow-x: hidden;
          margin: 0;
        }

        * { box-sizing: border-box; margin: 0; padding: 0; }

        /* NAV */
        .tg-nav {
          position: sticky;
          top: 0;
          z-index: 100;
          background: rgba(15,23,42,0.92);
          backdrop-filter: blur(12px);
          border-bottom: 1px solid var(--navy2);
          padding: 0 3rem;
          display: flex;
          align-items: center;
          justify-content: space-between;
          height: 60px;
          padding-top: env(safe-area-inset-top);
        }
        .tg-nav-logo {
          font-family: 'Bebas Neue', sans-serif;
          font-size: 22px;
          letter-spacing: .06em;
          color: var(--bright);
          text-decoration: none;
          flex-shrink: 0;
        }
        .tg-nav-logo span { color: var(--sky); }
        .tg-nav-links {
          display: flex;
          align-items: center;
          gap: 2rem;
          list-style: none;
        }
        .tg-nav-links a {
          font-size: 13px;
          font-weight: 500;
          color: var(--muted);
          text-decoration: none;
          transition: color .15s;
        }
        .tg-nav-links a:hover { color: var(--bright); }
        .tg-nav-actions { display: flex; align-items: center; gap: 10px; flex-shrink: 0; }
        .tg-btn-ghost-nav {
          font-size: 13px;
          font-weight: 500;
          color: var(--dim);
          background: transparent;
          border: 1px solid var(--navy3);
          padding: 7px 16px;
          border-radius: 6px;
          cursor: pointer;
          text-decoration: none;
          transition: border-color .15s, color .15s;
        }
        .tg-btn-ghost-nav:hover { border-color: var(--slate); color: var(--bright); }
        .tg-btn-sky {
          font-size: 13px;
          font-weight: 600;
          color: var(--navy);
          background: var(--sky);
          border: none;
          padding: 7px 18px;
          border-radius: 6px;
          cursor: pointer;
          text-decoration: none;
          transition: opacity .15s;
        }
        .tg-btn-sky:hover { opacity: .88; }

        /* HERO — Photo Split */
        .tg-hero {
          position: relative;
          min-height: calc(100vh - 60px);
          background: #080f1c;
          overflow: hidden;
          display: flex;
          align-items: stretch;
          border-bottom: 1px solid var(--navy2);
        }

        /* Left text panel */
        .tg-hero-left {
          position: relative;
          z-index: 2;
          flex: 0 0 58%;
          padding: 5rem 4rem 5rem 5rem;
          display: flex;
          flex-direction: column;
          justify-content: center;
        }

        /* Photo panel — construction site bg + cut-out worker on top */
        .tg-hero-photo {
          position: absolute;
          right: 0;
          top: 0;
          bottom: 0;
          width: 56%;
          overflow: hidden;
          pointer-events: none;
        }
        /* Layer 1: construction site background photo */
        .tg-hero-bg {
          position: absolute;
          inset: 0;
          background-image: url('/hero-bg.jpg');
          background-size: cover;
          background-position: 30% top;
        }
        /* No foreground worker PNG — bg already contains the worker */
        .tg-hero-photo img { display: none; }
        /* Layer 3: gradient fades left edge into the dark navy bg */
        .tg-hero-photo-overlay {
          position: absolute;
          inset: 0;
          background: linear-gradient(
            to right,
            #080f1c 0%,
            rgba(8,15,28,.85) 12%,
            rgba(8,15,28,.3) 28%,
            transparent 45%
          );
          pointer-events: none;
        }

        /* Eyebrow */
        .tg-eyebrow {
          font-size: 11px;
          letter-spacing: .16em;
          text-transform: uppercase;
          color: #475569;
          margin-bottom: 1.75rem;
        }

        /* Headline */
        .tg-hero-headline {
          font-family: 'Bebas Neue', sans-serif;
          font-size: clamp(52px, 5.2vw, 82px);
          line-height: .95;
          letter-spacing: .01em;
          margin-bottom: 2rem;
          white-space: nowrap;
        }
        .tg-hl-white  { color: var(--bright); display: block; }
        .tg-hl-sky    { color: var(--sky);    display: inline; }

        /* Subtitle */
        .tg-hero-sub {
          font-size: 16px;
          color: #94a3b8;
          line-height: 1.75;
          max-width: 400px;
          margin-bottom: 2.5rem;
        }

        /* CTA buttons */
        .tg-hero-btns { display: flex; gap: 12px; flex-wrap: wrap; }

        .tg-btn-cta {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          background: var(--sky);
          color: var(--navy);
          font-family: 'DM Sans', sans-serif;
          font-size: 15px;
          font-weight: 700;
          padding: 14px 28px;
          border-radius: 8px;
          border: 2px solid transparent;
          cursor: pointer;
          text-decoration: none;
          transition: opacity .15s;
          line-height: 1;
        }
        .tg-btn-cta:hover { opacity: .88; }

        .tg-btn-hero-outline {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          background: transparent;
          color: #f1f5f9;
          font-family: 'DM Sans', sans-serif;
          font-size: 15px;
          font-weight: 600;
          padding: 14px 28px;
          border-radius: 8px;
          border: 1.5px solid rgba(255,255,255,.28);
          cursor: pointer;
          text-decoration: none;
          transition: border-color .15s;
          line-height: 1;
        }
        .tg-btn-hero-outline:hover { border-color: rgba(255,255,255,.65); }

        /* SECTIONS */
        .tg-section { padding: 5rem 4rem; border-bottom: 1px solid var(--navy2); }
        .tg-section-eyebrow {
          font-family: 'Bebas Neue', sans-serif;
          font-size: 13px;
          letter-spacing: .15em;
          color: var(--sky);
          margin-bottom: 1rem;
        }
        .tg-section-title {
          font-family: 'Bebas Neue', sans-serif;
          font-size: 52px;
          line-height: 1;
          color: var(--bright);
          margin-bottom: 1rem;
          letter-spacing: .01em;
        }
        .tg-section-sub {
          font-size: 15px;
          color: var(--slate);
          line-height: 1.75;
          max-width: 520px;
          margin-bottom: 3rem;
        }

        /* TRADES */
        .tg-trades-grid {
          display: grid;
          grid-template-columns: repeat(4, 1fr);
          gap: 12px;
          margin-bottom: 2rem;
        }
        .tg-trade-card {
          background: var(--navy2);
          border: 1px solid var(--navy3);
          border-radius: 10px;
          padding: 20px 18px;
          text-decoration: none;
          display: flex;
          align-items: center;
          gap: 14px;
          transition: border-color .15s, background .15s;
        }
        .tg-trade-card:hover { border-color: var(--slate); background: #1a2a3a; }
        .tg-trade-icon {
          width: 40px; height: 40px;
          border-radius: 10px;
          display: flex; align-items: center; justify-content: center;
          font-size: 18px;
          flex-shrink: 0;
        }
        .tg-trade-name { font-size: 14px; font-weight: 500; color: var(--light); }
        .tg-trade-arrow { font-size: 12px; color: var(--navy3); margin-top: 2px; }
        .tg-view-all {
          font-size: 13px;
          font-weight: 500;
          color: var(--sky);
          text-decoration: none;
        }
        .tg-view-all:hover { opacity: .8; }

        /* HOW IT WORKS */
        .tg-steps-grid {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          border: 1px solid var(--navy2);
          border-radius: 12px;
          overflow: hidden;
        }
        .tg-step {
          background: var(--navy2);
          padding: 2.5rem;
        }
        .tg-step:not(:last-child) { border-right: 1px solid var(--navy3); }
        .tg-step-num {
          font-family: 'Bebas Neue', sans-serif;
          font-size: 64px;
          color: var(--navy3);
          line-height: 1;
          margin-bottom: 1rem;
        }
        .tg-step-title {
          font-family: 'Bebas Neue', sans-serif;
          font-size: 26px;
          color: var(--bright);
          margin-bottom: 0.75rem;
          letter-spacing: .01em;
        }
        .tg-step-text { font-size: 14px; color: var(--slate); line-height: 1.7; }

        /* AUDIENCE */
        .tg-audience-grid {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 16px;
        }
        .tg-audience-card {
          background: var(--navy2);
          border: 1px solid var(--navy3);
          border-radius: 12px;
          padding: 2.5rem;
          display: flex;
          flex-direction: column;
        }
        .tg-audience-icon {
          width: 48px; height: 48px;
          border-radius: 12px;
          display: flex; align-items: center; justify-content: center;
          margin-bottom: 1.5rem;
        }
        .tg-audience-who {
          font-size: 10px;
          letter-spacing: .1em;
          text-transform: uppercase;
          color: var(--navy3);
          margin-bottom: 6px;
        }
        .tg-audience-title {
          font-family: 'Bebas Neue', sans-serif;
          font-size: 30px;
          color: var(--bright);
          margin-bottom: 1rem;
          letter-spacing: .01em;
          line-height: 1.1;
        }
        .tg-audience-text {
          font-size: 14px;
          color: var(--slate);
          line-height: 1.7;
          margin-bottom: 1.5rem;
        }
        .tg-audience-list {
          list-style: none;
          margin-bottom: 2rem;
          flex: 1;
        }
        .tg-audience-list li {
          font-size: 13px;
          color: var(--dim);
          padding: 7px 0;
          border-bottom: 1px solid var(--navy3);
          display: flex;
          align-items: center;
          gap: 10px;
        }
        .tg-audience-list li:last-child { border-bottom: none; }
        .tg-li-dot { width: 6px; height: 6px; border-radius: 50%; flex-shrink: 0; }
        .tg-btn-audience {
          font-family: 'DM Sans', sans-serif;
          font-size: 13px;
          font-weight: 600;
          padding: 12px 20px;
          border-radius: 6px;
          border: none;
          cursor: pointer;
          text-decoration: none;
          display: inline-block;
          text-align: center;
          transition: opacity .15s;
        }
        .tg-btn-audience:hover { opacity: .85; }

        /* INSIGHTS */
        .tg-insights-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 16px; }
        .tg-insight-card {
          background: var(--navy2);
          border: 1px solid var(--navy3);
          border-radius: 12px;
          padding: 2rem;
          text-decoration: none;
          display: block;
          transition: border-color .15s;
        }
        .tg-insight-card:hover { border-color: var(--slate); }
        .tg-insight-tag {
          display: inline-block;
          font-size: 10px;
          letter-spacing: .08em;
          text-transform: uppercase;
          color: var(--sky);
          background: rgba(56,189,248,.08);
          padding: 4px 10px;
          border-radius: 20px;
          margin-bottom: 1rem;
        }
        .tg-insight-title {
          font-family: 'Bebas Neue', sans-serif;
          font-size: 24px;
          color: var(--bright);
          line-height: 1.15;
          letter-spacing: .01em;
          margin-bottom: 0.75rem;
        }
        .tg-insight-excerpt {
          font-size: 13px;
          color: var(--slate);
          line-height: 1.65;
          margin-bottom: 1.25rem;
        }
        .tg-insight-meta {
          font-size: 12px;
          color: var(--navy3);
          display: flex;
          align-items: center;
          gap: 8px;
        }

        /* CTA BAND */
        .tg-cta-band {
          padding: 6rem 4rem;
          text-align: center;
          border-bottom: 1px solid var(--navy2);
        }
        .tg-cta-headline {
          font-family: 'Bebas Neue', sans-serif;
          font-size: 72px;
          line-height: 1;
          color: var(--bright);
          letter-spacing: .01em;
          margin-bottom: 1.25rem;
        }
        .tg-cta-headline span { color: var(--sky); }
        .tg-cta-sub {
          font-size: 16px;
          color: var(--slate);
          margin-bottom: 2.5rem;
          max-width: 480px;
          margin-left: auto;
          margin-right: auto;
          line-height: 1.75;
        }
        .tg-cta-btns {
          display: flex;
          gap: 12px;
          justify-content: center;
          align-items: center;
          flex-wrap: wrap;
        }
        .tg-btn-outline-sky {
          font-size: 14px;
          font-weight: 500;
          color: var(--sky);
          background: transparent;
          border: 1px solid rgba(56,189,248,.3);
          padding: 13px 28px;
          border-radius: 6px;
          cursor: pointer;
          text-decoration: none;
          transition: border-color .15s;
        }
        .tg-btn-outline-sky:hover { border-color: var(--sky); }

        /* FOOTER */
        .tg-footer {
          padding: 4rem;
          display: grid;
          grid-template-columns: 1.5fr 1fr 1fr 1fr 1fr;
          gap: 3rem;
        }
        .tg-footer-brand p {
          font-size: 13px;
          color: var(--slate);
          line-height: 1.7;
          margin-top: 0.75rem;
          max-width: 220px;
        }
        .tg-footer-col-title {
          font-family: 'Bebas Neue', sans-serif;
          font-size: 14px;
          letter-spacing: .08em;
          color: var(--bright);
          margin-bottom: 1rem;
        }
        .tg-footer-links { list-style: none; }
        .tg-footer-links li { margin-bottom: 0.5rem; }
        .tg-footer-links a {
          font-size: 13px;
          color: var(--slate);
          text-decoration: none;
          transition: color .15s;
        }
        .tg-footer-links a:hover { color: var(--bright); }
        .tg-footer-bottom {
          padding: 1.5rem 4rem;
          padding-bottom: calc(1.5rem + env(safe-area-inset-bottom));
          border-top: 1px solid var(--navy2);
          display: flex;
          align-items: center;
          justify-content: space-between;
        }
        .tg-footer-bottom p { font-size: 12px; color: var(--navy3); }

        /* ── MOBILE RESPONSIVE ── */
        @media (max-width: 768px) {
          /* Nav */
          .tg-nav { padding: 0 1.25rem; height: 56px; }
          .tg-nav-links { display: none; }
          .tg-btn-ghost-nav { display: none; }
          .tg-btn-sky { padding: 8px 14px; font-size: 12px; }

          /* Hero */
          .tg-hero { min-height: 80vh; }
          .tg-hero-left {
            flex: 0 0 100%;
            padding: 3.5rem 1.5rem 2.5rem;
            background: linear-gradient(to bottom, #080f1c 70%, rgba(8,15,28,.85) 100%);
          }
          .tg-hero-photo {
            clip-path: none;
            width: 100%;
            opacity: .35;
          }
          .tg-hl-white, .tg-hl-sky { font-size: 60px; }
          .tg-hero-sub { font-size: 15px; max-width: 100%; }
          .tg-hero-btns { gap: 10px; }
          .tg-btn-cta, .tg-btn-hero-outline { font-size: 14px; padding: 13px 22px; }

          /* Sections */
          .tg-section { padding: 3rem 1.25rem; }
          .tg-section-title { font-size: 36px; }
          .tg-section-sub { font-size: 14px; margin-bottom: 2rem; }

          /* Trades */
          .tg-trades-grid { grid-template-columns: 1fr 1fr; gap: 8px; }
          .tg-trade-card { padding: 14px 12px; gap: 10px; }
          .tg-trade-icon { width: 32px; height: 32px; font-size: 15px; }
          .tg-trade-name { font-size: 13px; }

          /* Steps */
          .tg-steps-grid { grid-template-columns: 1fr; border-radius: 10px; }
          .tg-step { padding: 1.75rem 1.5rem; }
          .tg-step:not(:last-child) { border-right: none; border-bottom: 1px solid var(--navy3); }
          .tg-step-num { font-size: 48px; margin-bottom: 0.75rem; }

          /* Audience */
          .tg-audience-grid { grid-template-columns: 1fr; }
          .tg-audience-card { padding: 1.75rem 1.5rem; }

          /* Insights */
          .tg-insights-grid { grid-template-columns: 1fr; }
          .tg-insight-card { padding: 1.5rem; }
          .tg-insight-title { font-size: 20px; }

          /* CTA */
          .tg-cta-band { padding: 3.5rem 1.25rem; }
          .tg-cta-headline { font-size: 44px; }
          .tg-cta-sub { font-size: 14px; }
          .tg-cta-btns { flex-direction: column; align-items: stretch; }
          .tg-btn-cta, .tg-btn-outline-sky { text-align: center; width: 100%; }

          /* Footer */
          .tg-footer {
            grid-template-columns: 1fr 1fr;
            padding: 2.5rem 1.25rem;
            gap: 2rem;
          }
          .tg-footer-brand { grid-column: 1 / -1; }
          .tg-footer-bottom {
            padding: 1.25rem 1.25rem;
            padding-bottom: calc(1.25rem + env(safe-area-inset-bottom));
            flex-direction: column;
            gap: 6px;
            text-align: center;
          }
        }

        @media (max-width: 480px) {
          .tg-hl-white, .tg-hl-sky { font-size: 48px; }
          .tg-section-title { font-size: 30px; }
          .tg-cta-headline { font-size: 36px; }
        }
      `}</style>

      {/* ── NAV ── */}
      <nav className="tg-nav">
        <Link href="/" className="tg-nav-logo">
          Blue Collar<span> Social</span>
        </Link>
        <ul className="tg-nav-links">
          <li><Link href="/jobs">Browse Jobs</Link></li>
          <li><Link href="/directory">Find Tradespeople</Link></li>
          <li><Link href="/blog">Insights</Link></li>
        </ul>
        <div className="tg-nav-actions">
          <Link href="/login" className="tg-btn-ghost-nav">Log In</Link>
          <Link href="/register" className="tg-btn-sky">Get Started</Link>
        </div>
      </nav>

      {/* ── HERO ── */}
      <section className="tg-hero">

        {/* Left — text */}
        <div className="tg-hero-left">
          <div className="tg-eyebrow">The Professional Network For</div>

          <div className="tg-hero-headline">
            <span className="tg-hl-white">THE PEOPLE</span>
            <span className="tg-hl-white">WHO BUILD</span>
            <span className="tg-hl-white">THE <span className="tg-hl-sky" style={{ display: "inline" }}>REAL WORLD</span></span>
          </div>

          <p className="tg-hero-sub">
            Connect with tradespeople, jobs, and real opportunities.
          </p>

          <div className="tg-hero-btns">
            <Link href="/jobs" className="tg-btn-cta">
              {/* Briefcase icon */}
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <rect x="2" y="7" width="20" height="14" rx="2"/>
                <path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16"/>
              </svg>
              Find Jobs
            </Link>
            <Link href="/jobs/new" className="tg-btn-hero-outline">
              {/* Person-plus icon */}
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"/>
                <circle cx="9" cy="7" r="4"/>
                <line x1="19" y1="8" x2="19" y2="14"/>
                <line x1="22" y1="11" x2="16" y2="11"/>
              </svg>
              Post a Job
            </Link>
            <Link href="/register" className="tg-btn-hero-outline">
              {/* Clipboard icon */}
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2"/>
                <rect x="8" y="2" width="8" height="4" rx="1" ry="1"/>
              </svg>
              Post a Project
            </Link>
          </div>
        </div>

        {/* Right — construction site bg + cut-out worker layered on top */}
        <div className="tg-hero-photo" aria-hidden="true">
          <div className="tg-hero-bg" />
          {/* eslint-disable-next-line @next/next/no-img-element */}
          <img src="/hero-worker.png" alt="" />
          <div className="tg-hero-photo-overlay" />
        </div>

      </section>

      {/* ── BROWSE BY TRADE ── */}
      <section className="tg-section">
        <div className="tg-section-eyebrow">Browse By Trade</div>
        <div className="tg-section-title">Find Your Specialty.</div>
        <p className="tg-section-sub">Explore tradespeople and jobs across 15+ trades — from electrical and plumbing to landscaping and beyond.</p>
        <div className="tg-trades-grid">
          {[
            { href: "/jobs/plumbing",    icon: "🔧", bg: "#1a2a1a", label: "Plumber" },
            { href: "/jobs/electrical",  icon: "⚡", bg: "#1a1a2a", label: "Electrician" },
            { href: "/jobs/carpentry",   icon: "🪚", bg: "#2a1a10", label: "Carpenter" },
            { href: "/jobs/roofing",     icon: "🏠", bg: "#1a1a2a", label: "Roofer" },
            { href: "/jobs/hvac",        icon: "❄️", bg: "#0a1a2a", label: "HVAC Technician" },
            { href: "/jobs/painting",    icon: "🖌️", bg: "#1a1020", label: "Painter" },
            { href: "/jobs/flooring",    icon: "🪵", bg: "#2a1a10", label: "Flooring Installer" },
            { href: "/jobs/landscaping", icon: "🌿", bg: "#0a1a0a", label: "Landscaper" },
          ].map((trade) => (
            <Link key={trade.href} href={trade.href} className="tg-trade-card">
              <div className="tg-trade-icon" style={{ background: trade.bg }}>{trade.icon}</div>
              <div>
                <div className="tg-trade-name">{trade.label}</div>
                <div className="tg-trade-arrow">View Jobs →</div>
              </div>
            </Link>
          ))}
        </div>
        <Link href="/jobs" className="tg-view-all">View All 15 Trades →</Link>
      </section>

      {/* ── HOW IT WORKS ── */}
      <section className="tg-section">
        <div className="tg-section-eyebrow">How It Works</div>
        <div className="tg-section-title">Get Connected In Minutes.</div>
        <p className="tg-section-sub">Whether you&apos;re posting a job, building your profile, or hiring a crew — it takes minutes to get started.</p>
        <div className="tg-steps-grid">
          {[
            { num: "01", title: "Post Or Browse Jobs", text: "Homeowners post jobs for free. Tradespeople browse open listings and apply to the ones that fit their skills and location." },
            { num: "02", title: "Connect Directly", text: "Message tradespeople, review their profiles and real project history, and accept applications — all in one place, no middlemen." },
            { num: "03", title: "Build Your Reputation", text: "Hire with confidence. Tradespeople grow their network, collect reviews, and build a profile that does the selling for them." },
          ].map((step) => (
            <div key={step.num} className="tg-step">
              <div className="tg-step-num">{step.num}</div>
              <div className="tg-step-title">{step.title}</div>
              <p className="tg-step-text">{step.text}</p>
            </div>
          ))}
        </div>
      </section>

      {/* ── WHO IT'S FOR ── */}
      <section className="tg-section">
        <div className="tg-section-eyebrow">Who It&apos;s For</div>
        <div className="tg-section-title">Three Ways To Use It.</div>
        <p className="tg-section-sub">One platform — built for tradespeople, homeowners, and the companies that hire them.</p>
        <div className="tg-audience-grid">
          <div className="tg-audience-card">
            <div className="tg-audience-icon" style={{ background: "var(--sky-bg)" }}>
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#38BDF8" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
                <path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z"/>
              </svg>
            </div>
            <div className="tg-audience-who">For Tradespeople</div>
            <div className="tg-audience-title">Build Your Name.</div>
            <p className="tg-audience-text">Grow your client base and professional reputation — all from a single profile that showcases your real work.</p>
            <ul className="tg-audience-list">
              {["Create a Professional Profile — Free", "Browse And Apply For Local Jobs", "Build A Network Of Connections", "Showcase Skills And Past Projects"].map((item) => (
                <li key={item}><span className="tg-li-dot" style={{ background: "var(--sky)" }} />{item}</li>
              ))}
            </ul>
            <Link href="/register/tradesperson" className="tg-btn-audience tg-btn-sky">Create Your Profile →</Link>
          </div>

          <div className="tg-audience-card">
            <div className="tg-audience-icon" style={{ background: "var(--grn-bg)" }}>
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#4ADE80" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
                <path d="m3 9 9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/>
                <polyline points="9 22 9 12 15 12 15 22"/>
              </svg>
            </div>
            <div className="tg-audience-who">For Homeowners</div>
            <div className="tg-audience-title">Hire With Confidence.</div>
            <p className="tg-audience-text">Find the right tradesperson for any project, big or small — with real profiles, real reviews, and direct messaging.</p>
            <ul className="tg-audience-list">
              {["Post Jobs In Minutes — Completely Free", "Browse Verified Tradespeople Near You", "Message Applicants Directly", "See Their Actual Work, Not Just A Bio"].map((item) => (
                <li key={item}><span className="tg-li-dot" style={{ background: "var(--green)" }} />{item}</li>
              ))}
            </ul>
            <Link href="/register/homeowner" className="tg-btn-audience" style={{ background: "var(--green)", color: "var(--navy)" }}>Post A Job Free →</Link>
          </div>

          <div className="tg-audience-card">
            <div className="tg-audience-icon" style={{ background: "var(--amb-bg)" }}>
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#F59E0B" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
                <rect x="2" y="7" width="20" height="14" rx="2"/>
                <path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16"/>
              </svg>
            </div>
            <div className="tg-audience-who">For Employers & Companies</div>
            <div className="tg-audience-title">Find Reliable Crews.</div>
            <p className="tg-audience-text">Skip the résumé pile. Find workers with proven track records, real reviews, and the skills you need on the job.</p>
            <ul className="tg-audience-list">
              {["Post Jobs And Reach Active Workers", "Filter By Trade, Location, And Experience", "Review Real Project Portfolios", "Message And Hire Directly"].map((item) => (
                <li key={item}><span className="tg-li-dot" style={{ background: "var(--amber)" }} />{item}</li>
              ))}
            </ul>
            <Link href="/register" className="tg-btn-audience" style={{ background: "var(--amber)", color: "var(--navy)" }}>Start Hiring →</Link>
          </div>
        </div>
      </section>

      {/* ── INSIGHTS ── */}
      <section className="tg-section">
        <div style={{ display: "flex", alignItems: "flex-end", justifyContent: "space-between", marginBottom: "3rem", flexWrap: "wrap", gap: "1rem" }}>
          <div>
            <div className="tg-section-eyebrow">Insights</div>
            <div className="tg-section-title" style={{ marginBottom: 0 }}>From The Trades.</div>
          </div>
          <Link href="/blog" className="tg-view-all">View All Posts →</Link>
        </div>
        <div className="tg-insights-grid">
          <Link href="/blog/what-alex-karp-gets-right" className="tg-insight-card">
            <span className="tg-insight-tag">AI & The Future Of Work</span>
            <div className="tg-insight-title">What Alex Karp Gets Right — And Wrong — About Who Will Survive The AI Era</div>
            <p className="tg-insight-excerpt">Palantir&apos;s CEO compressed a large anxiety into a sharp soundbite. Buried inside is a real conversation about what the AI era rewards — and what our talent pipeline has gotten wrong.</p>
            <div className="tg-insight-meta">
              <span>Q1 2026</span>
              <span style={{ color: "var(--navy3)" }}>·</span>
              <span>6 Min Read</span>
            </div>
          </Link>
          <Link href="/blog/the-skilled-labor-shortage" className="tg-insight-card">
            <span className="tg-insight-tag">Workforce & Labor Markets</span>
            <div className="tg-insight-title">The Skilled Labor Shortage Is Getting Worse — But A New Platform Aims To Fix The Disconnect</div>
            <p className="tg-insight-excerpt">The skilled labor shortage is often framed as a supply problem. But the deeper issue may be labor-market disconnect — and better infrastructure could help fix it.</p>
            <div className="tg-insight-meta">
              <span>Q4 2025</span>
              <span style={{ color: "var(--navy3)" }}>·</span>
              <span>7 Min Read</span>
            </div>
          </Link>
        </div>
      </section>

      {/* ── CTA BAND ── */}
      <div className="tg-cta-band">
        <div className="tg-cta-headline">Ready To <span>Get Started?</span></div>
        <p className="tg-cta-sub">Join thousands of tradespeople, homeowners, and employers already using Blue Collar Social to connect and get work done.</p>
        <div className="tg-cta-btns">
          <Link href="/register" className="tg-btn-cta" style={{ fontSize: "15px", padding: "15px 36px" }}>Create A Free Account →</Link>
          <Link href="/directory" className="tg-btn-outline-sky">Browse Tradespeople</Link>
        </div>
      </div>

      {/* ── FOOTER ── */}
      <footer className="tg-footer">
        <div className="tg-footer-brand">
          <div style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: "20px", letterSpacing: ".06em", color: "var(--bright)" }}>
            Blue Collar<span style={{ color: "var(--sky)" }}> Social</span>
          </div>
          <p>Connecting skilled tradespeople with homeowners and employers across the US.</p>
        </div>
        <div>
          <div className="tg-footer-col-title">For Homeowners</div>
          <ul className="tg-footer-links">
            <li><Link href="/jobs/new">Post A Job</Link></li>
            <li><Link href="/directory">Find Tradespeople</Link></li>
            <li><Link href="/register/homeowner">Sign Up</Link></li>
          </ul>
        </div>
        <div>
          <div className="tg-footer-col-title">For Tradespeople</div>
          <ul className="tg-footer-links">
            <li><Link href="/jobs">Browse Jobs</Link></li>
            <li><Link href="/register/tradesperson">Create Profile</Link></li>
            <li><Link href="/login">Log In</Link></li>
          </ul>
        </div>
        <div>
          <div className="tg-footer-col-title">Resources</div>
          <ul className="tg-footer-links">
            <li><Link href="/blog">Insights</Link></li>
            <li><Link href="/contact">Contact</Link></li>
            <li><Link href="/privacy">Privacy Policy</Link></li>
            <li><Link href="/terms">Terms of Service</Link></li>
          </ul>
        </div>
        <div>
          <div className="tg-footer-col-title">Trades</div>
          <ul className="tg-footer-links">
            <li><Link href="/jobs/plumbing">Plumbing</Link></li>
            <li><Link href="/jobs/electrical">Electrical</Link></li>
            <li><Link href="/jobs/carpentry">Carpentry</Link></li>
            <li><Link href="/jobs/roofing">Roofing</Link></li>
            <li><Link href="/jobs">All Trades →</Link></li>
          </ul>
        </div>
      </footer>
      <div className="tg-footer-bottom">
        <p>© 2026 Blue Collar Social. All Rights Reserved.</p>
        <p style={{ color: "var(--navy3)" }}>Built For The People Who Build.</p>
      </div>
    </>
  );
}
